import type { ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Logo } from '@rydres/ui';
import { useRiderBar } from './lib/passenger.js';
import { BookingPage } from './screens/BookingPage.js';
import { Landing } from './screens/Landing.js';
import { Privacy, Terms } from './screens/Legal.js';
import { AccountPage, JoinPage } from './screens/Passenger.js';
import { PeoplePage } from './screens/People.js';
import { HomePage } from './screens/RiderHome.js';
import { ServicesPage } from './screens/RiderServices.js';
import { RiderTabs } from './screens/RiderTabs.js';
import { QuotePage } from './screens/QuotePage.js';
import { StatusPage } from './screens/StatusPage.js';
import { WaybillRedirect } from './screens/WaybillRedirect.js';

/**
 * A thin bar on every page: for a signed-in passenger the brand and, on a wide screen, their tabs;
 * otherwise a link to the account. The legal pages carry their own.
 */
function SiteHeader({ rider }: { rider: { signedIn: boolean; unread: number } }) {
  const { pathname } = useLocation();
  if (pathname === '/privacy' || pathname === '/terms') return null;
  return (
    <header className="mx-auto flex max-w-xl items-center justify-between gap-4 px-4 pt-3 text-xs">
      {rider.signedIn || pathname !== '/' ? (
        <Link to={rider.signedIn ? '/me' : '/'} className="flex min-h-11 items-center">
          <Logo size={24} className="text-[15px]" />
        </Link>
      ) : (
        <span />
      )}
      {rider.signedIn ? <RiderTabs unread={rider.unread} placement="top" /> : pathname !== '/me' && <Link to="/me" className="font-bold text-accent-text">Your account</Link>}
    </header>
  );
}

/** Leaves room on the right of a wide screen while a conversation is open there (see `ChatSheet`), header included. */
function Frame({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const rider = useRiderBar(pathname);
  const tabs = rider.signedIn && pathname !== '/privacy' && pathname !== '/terms';
  const chat = pathname === '/me/people' && params.has('chat');
  return (
    <div className={[chat && 'lg:pr-[440px]', tabs && 'pb-20 md:pb-0'].filter(Boolean).join(' ') || undefined}>
      <SiteHeader rider={rider} />
      {children}
      {tabs && <RiderTabs unread={rider.unread} placement="bottom" />}
    </div>
  );
}

function OldLink({ to }: { to: 'with' | 'chat' }) {
  const { handle = '', id = '' } = useParams();
  return <Navigate to={`/me/people?${to}=${encodeURIComponent(to === 'with' ? handle : id)}`} replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <Frame>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/b/:token" element={<StatusPage />} />
          <Route path="/q/:token" element={<QuotePage />} />
          <Route path="/w/:token" element={<WaybillRedirect />} />
          <Route path="/me" element={<HomePage />} />
          <Route path="/me/services" element={<ServicesPage />} />
          <Route path="/me/people" element={<PeoplePage />} />
          <Route path="/me/account" element={<AccountPage />} />
          {/* Links from before messages moved into People: texts already sent, older status pages. */}
          <Route path="/me/messages" element={<Navigate to="/me/people" replace />} />
          <Route path="/me/messages/with/:handle" element={<OldLink to="with" />} />
          <Route path="/me/messages/:id" element={<OldLink to="chat" />} />
          <Route path="/:handle/join" element={<JoinPage />} />
          <Route path="/:handle" element={<BookingPage />} />
        </Routes>
      </Frame>
    </BrowserRouter>
  );
}
